import { call, put, takeLatest, select } from 'redux-saga/effects';
import { 
    fetchModelsFailure, 
    fetchModelsStart, 
    fetchModelsSuccess, 
    loadModelsRequest,
    addCatalogueRequest,
    addCatalogueStart,
    addCatalogueFailure,
    updateCatalogueRequest,
    updateCatalogueStart,
    updateCatalogueSuccess,
    updateCatalogueFailure,
    deleteCatalogueRequest,
    deleteCatalogueStart,
    deleteCatalogueSuccess,
    deleteCatalogueFailure
} from '../modelsSlice';
import { getModelsApi } from '../../api/apiLists/getModelsApi';
import { addCatalogueApi, updateCatalogueApi, deleteCatalogueApi } from '../../api/apiLists/catalogueApi';
import { callWithAuth } from '../sagaApiHelper';
import { showAlert } from '../alertSlice';
import { closeDialog } from '../dialogSlice';

function* handleFetchModels(action) {
    try {
        yield put(fetchModelsStart());
    // payload may include limit and nextToken
    const { payload } = action || {};
    const res = yield call(callWithAuth, getModelsApi, [payload || {}]);
                
        if (res.status && res.data.items) {
            const models = res.data.items.map(item => ({ 
                label: item.name, 
                value: item.id, 
                extendedWarranty: item.extWarrantyPeriodInDays,
                warrantyPeriodInDays: item.warrantyPeriodInDays,
                extWarrantyPeriodInDays: item.extWarrantyPeriodInDays
            }));
            yield put(fetchModelsSuccess({
                models,
                items: res.data.items,
                nextToken: res.data.nextToken,
                count: res.data.count
            }));
        } else {
            yield put(fetchModelsFailure({ error: res.errMsg || 'Failed to fetch models' }));
        }
    } catch (error) {
        yield put(fetchModelsFailure({ error: error.message }));
    }
}

function* handleAddCatalogue(action) {
    try {
        yield put(addCatalogueStart());
        const res = yield call(callWithAuth, addCatalogueApi, [action.payload]);
        
        if (res.status && res.data.item) {
            yield put(loadModelsRequest({ limit: 10, nextToken: null }));
            yield put(showAlert({ 
                type: 'success', 
                message: 'Catalogue added successfully' 
            }));
            yield put(closeDialog('addCatalogue'));
        } else {
            yield put(addCatalogueFailure({ error: res.errMsg || 'Failed to add catalogue' }));
            yield put(showAlert({ 
                type: 'error', 
                message: res.errMsg || 'Failed to add catalogue' 
            }));
        }
    } catch (error) {
        yield put(addCatalogueFailure({ error: error.message }));
        yield put(showAlert({ 
            type: 'error', 
            message: error.message 
        }));
    }
}

function* handleUpdateCatalogue(action) {
    try {
        const payload = action.payload || {};
        const items = yield select(state => (state.models && state.models.items) || []);
        const original = items.find(i => i.id === payload.id || i.id === payload.itemId);

        const normalize = (v) => {
            if (v === undefined || v === null) return v;
            if (typeof v === 'object') {
                if (v.hasOwnProperty('value')) return v.value;
                try { return JSON.stringify(v); } catch (e) { return String(v); }
            }
            return String(v);
        };

        const buildDiff = (orig, pl) => {
            const diff = {};
            const id = pl.id || pl.itemId || (orig && orig.id);
            if (id) diff.id = id;

            for (const key of Object.keys(pl)) {
                if (key === 'id' || key === 'itemId') continue;

                if (key === 'categoryObject') {
                    const newVal = pl.categoryObject && pl.categoryObject.value;
                    const oldVal = orig && orig.category;
                    if (normalize(newVal) !== normalize(oldVal)) diff.category = newVal;
                    continue;
                }
                if (key === 'subCategoryObject') {
                    const newVal = pl.subCategoryObject && pl.subCategoryObject.value;
                    const oldVal = orig && orig.subCategory;
                    if (normalize(newVal) !== normalize(oldVal)) diff.subCategory = newVal;
                    continue;
                }

                const newVal = pl[key];
                const oldVal = orig && (orig[key] !== undefined ? orig[key] : undefined);

                if (normalize(newVal) !== normalize(oldVal)) {
                    diff[key] = newVal;
                }
            }

            return diff;
        };

        const updatePayload = buildDiff(original, payload);

        const keysToUpdate = Object.keys(updatePayload).filter(k => k !== 'id');
        if (keysToUpdate.length === 0) {
            yield put(showAlert({ type: 'error', message: 'No changes detected' }));
            return;
        }

        yield put(updateCatalogueStart());
        const res = yield call(callWithAuth, updateCatalogueApi, [updatePayload]);

        if (res.status && res.data.item) {
            yield put(updateCatalogueSuccess({ item: res.data.item }));
            yield put(showAlert({ 
                type: 'success', 
                message: 'Catalogue updated successfully' 
            }));
            yield put(closeDialog('editCatalogue'));
        } else {
            yield put(updateCatalogueFailure({ error: res.errMsg || 'Failed to update catalogue' }));
            yield put(showAlert({ 
                type: 'error', 
                message: res.errMsg || 'Failed to update catalogue' 
            }));
        }
    } catch (error) {
        yield put(updateCatalogueFailure({ error: error.message }));
        yield put(showAlert({ 
            type: 'error', 
            message: error.message 
        }));
    }
}

function* handleDeleteCatalogue(action) {
    try {
        yield put(deleteCatalogueStart());
        const catalogueId = action.payload;
        const res = yield call(callWithAuth, deleteCatalogueApi, [catalogueId]);
        
        if (res.status) {
            yield put(deleteCatalogueSuccess({ id: catalogueId }));
            yield put(showAlert({ 
                type: 'success', 
                message: 'Catalogue deleted successfully' 
            }));
        } else {
            yield put(deleteCatalogueFailure({ error: res.errMsg || 'Failed to delete catalogue' }));
            yield put(showAlert({ 
                type: 'error', 
                message: res.errMsg || 'Failed to delete catalogue' 
            }));
        }
    } catch (error) {
        yield put(deleteCatalogueFailure({ error: error.message }));
        yield put(showAlert({ 
            type: 'error', 
            message: error.message 
        }));
    }
}

export default function* modelsSaga() {
    yield takeLatest(loadModelsRequest.type, handleFetchModels);
    yield takeLatest(addCatalogueRequest.type, handleAddCatalogue);
    yield takeLatest(updateCatalogueRequest.type, handleUpdateCatalogue);
    yield takeLatest(deleteCatalogueRequest.type, handleDeleteCatalogue);
}